import React, { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { downloadBlob } from "@/lib/download";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { FileText, Download, Search, Upload, Trash2, Loader2, FileLock2, FolderArchive, BarChart3, Lock, NotebookPen } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["All", "Governance", "Financial Reports", "Maritime Law", "Galleon Bylaws", "Event Gallery", "Meeting Minutes", "General"];
const KIND_TABS = [
  { key: "document", label: "Documents", icon: FileText },
  { key: "resource", label: "Resource Archive", icon: FolderArchive },
  { key: "report", label: "Reports", icon: BarChart3 },
  { key: "minutes", label: "Meeting Minutes", icon: NotebookPen },
];
const roleLabel = { member: "All Members", officer: "Officers+", super_admin: "Super Admin" };

function UploadDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ title: "", category: "General", kind: "document", min_role: "member", description: "" });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file || !form.title) { toast.error("Title and file are required"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      await api.post("/documents", fd);
      toast.success("Document uploaded");
      setOpen(false); setFile(null);
      setForm({ title: "", category: "General", kind: "document", min_role: "member", description: "" });
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-[hsl(var(--gold))] font-700 text-[#0A192F]" data-testid="upload-document-button">
          <Upload className="mr-2 h-4 w-4" /> Upload
        </Button>
      </DialogTrigger>
      <DialogContent className="glass">
        <DialogHeader><DialogTitle className="font-display">Upload Document</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="doc-title-input" /></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1" data-testid="doc-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.filter((c) => c !== "All").map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="document">Document</SelectItem>
                  <SelectItem value="resource">Resource</SelectItem>
                  <SelectItem value="report">Report</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Access Level</Label>
            <Select value={form.min_role} onValueChange={(v) => setForm({ ...form, min_role: v })}>
              <SelectTrigger className="mt-1" data-testid="doc-access-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="member">All Members</SelectItem>
                <SelectItem value="officer">Officers & above</SelectItem>
                <SelectItem value="super_admin">Super Admin only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>File</Label><Input type="file" onChange={(e) => setFile(e.target.files?.[0])} className="mt-1" data-testid="doc-file-input" /></div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} className="bg-[hsl(var(--gold))] text-[#0A192F]" data-testid="doc-submit-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Library() {
  const { hasRole } = useAuth();
  const [kind, setKind] = useState("document");
  const [category, setCategory] = useState("All");
  const [q, setQ] = useState("");
  const [docs, setDocs] = useState([]);

  const load = () => api.get("/documents", { params: { kind, category, q } }).then((r) => setDocs(r.data.documents));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, category]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const del = async (id) => { await api.delete(`/documents/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <PageHeader eyebrow="Repository" title="Document Library"
        description="Official documents, searchable resources, and permission-gated reports.">
        {hasRole("officer") && <UploadDialog onDone={load} />}
      </PageHeader>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <Tabs value={kind} onValueChange={setKind}>
          <TabsList className="glass">
            {KIND_TABS.map(({ key, label, icon: Icon }) => (
              <TabsTrigger key={key} value={key} data-testid={`library-tab-${key}`}>
                <Icon className="mr-1.5 h-4 w-4" /> {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search title…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="library-search-input" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-[180px]" data-testid="library-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {docs.map((d) => (
          <Card key={d.id} className="gold-ring glass flex flex-col p-5" data-testid={`doc-card-${d.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[hsl(var(--gold))]/15 text-[hsl(var(--gold))]">
                <FileLock2 className="h-5 w-5" />
              </div>
              {d.min_role !== "member" && (
                <Badge variant="outline" className="border-[hsl(var(--water))]/40 text-[hsl(var(--water))]">
                  <Lock className="mr-1 h-3 w-3" /> {roleLabel[d.min_role]}
                </Badge>
              )}
            </div>
            <h3 className="mt-3 font-display text-lg font-700">{d.title}</h3>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{d.category}</p>
            {d.description && <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{d.description}</p>}
            <div className="mt-4 flex items-center gap-2 pt-2">
              <Button size="sm" className="flex-1 bg-[hsl(var(--water))] text-white" data-testid={`download-${d.id}`}
                onClick={() => downloadBlob(`/documents/${d.id}/download`, `${d.title}.${d.ext || "pdf"}`)}>
                <Download className="mr-1.5 h-4 w-4" /> Download
              </Button>
              {hasRole("officer") && (
                <Button size="icon" variant="ghost" className="text-destructive" onClick={() => del(d.id)} data-testid={`delete-doc-${d.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>
      {docs.length === 0 && <p className="text-sm text-muted-foreground">No items in this section yet.</p>}
    </div>
  );
}
